import { useState } from "react";

import { Close, CloudUpload, Delete } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDropzone } from "react-dropzone";

import { bytesToMb, validateFile } from "utils";

import { FILE_UPLOAD_ACCEPT } from "../../constants";

import type { FileRejection } from "react-dropzone";

interface Props {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  onFileErrors?: (errors: string[]) => void;
  onClose?: VoidFunction;
}

function getDropzoneLabel(atCapacity: boolean, isDragActive: boolean): string {
  if (atCapacity) return `Maximum files reached`;
  if (isDragActive) return "Drop files here...";
  return "Drag & drop files, or click to select";
}

export function FileDropzone(props: Props) {
  const { files, onFilesChange, maxFiles, onFileErrors, onClose } = props;

  const atCapacity = maxFiles !== undefined && files.length >= maxFiles;
  const [overflowCount, setOverflowCount] = useState<number>(0);

  const { fileRejections, getRootProps, getInputProps, isDragActive } =
    useDropzone({
      accept: FILE_UPLOAD_ACCEPT,
      validator: validateFile,
      onDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        if (acceptedFiles.length > 0) {
          const remaining =
            maxFiles !== undefined ? maxFiles - files.length : Infinity;
          const dropped =
            remaining !== Infinity && acceptedFiles.length > remaining
              ? acceptedFiles.length - remaining
              : 0;
          setOverflowCount(dropped);
          onFilesChange([
            ...files,
            ...acceptedFiles.slice(
              0,
              remaining === Infinity ? undefined : remaining,
            ),
          ]);
        } else {
          setOverflowCount(0);
        }
        if (rejectedFiles.length > 0 && onFileErrors) {
          const errorMessages = rejectedFiles.map(
            ({ file, errors }) =>
              `${file.name}: ${errors
                .filter(
                  (e) =>
                    e.code === "invalid_file_size" ||
                    e.code === "invalid_file_type",
                )
                .map((e) => e.message)
                .join(", ")}`,
          );
          onFileErrors(errorMessages);
        }
      },
      multiple: true,
      disabled: atCapacity,
    });

  const removeFile = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    setOverflowCount(0);
    onFilesChange(updated);
  };

  return (
    <Stack direction="column" spacing={1}>
      {onClose && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CloudUpload sx={{ color: "primary.main", fontSize: 28 }} />
            <Typography
              variant="body1"
              color="primary"
              sx={{ fontWeight: 500 }}
            >
              Upload files
            </Typography>
          </Stack>
          <Tooltip title="Close Documents">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close Documents"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {overflowCount > 0 && (
        <Alert severity="warning">
          {`${overflowCount} file${overflowCount > 1 ? "s were" : " was"} not added — limit of ${maxFiles} reached.`}
        </Alert>
      )}

      {fileRejections.length > 0 &&
        fileRejections.map(({ file, errors }) => (
          <Alert key={`error-${file.name}`} severity="error">
            {file.name}:{" "}
            {errors
              .filter(
                (e) =>
                  e.code === "invalid_file_size" ||
                  e.code === "invalid_file_type",
              )
              .map((e) => e.message)
              .join(", ")}
          </Alert>
        ))}

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          cursor: atCapacity ? "not-allowed" : "pointer",
          backgroundColor: isDragActive ? "action.hover" : "grey.50",
          transition: "all 0.2s ease",
          "&:hover": atCapacity
            ? {}
            : { borderColor: "primary.main", backgroundColor: "action.hover" },
        }}
      >
        <input {...getInputProps()} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          {!onClose && <CloudUpload sx={{ color: "grey.500" }} />}
          <Typography variant="body2" color="text.secondary">
            {getDropzoneLabel(atCapacity, isDragActive)}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {maxFiles !== undefined
            ? `Up to ${maxFiles} files · max 100 MB per file`
            : "Max 100 MB per file"}
        </Typography>
      </Box>

      {files.length > 0 && (
        <List dense disablePadding>
          {files.map((file, index) => (
            <ListItem
              key={`${file.name}-${file.size}-${file.lastModified}`}
              disableGutters
              secondaryAction={
                <Tooltip title="Remove file">
                  <IconButton
                    edge="end"
                    aria-label="remove file"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={file.name}
                secondary={`${bytesToMb(file.size)} MB`}
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                }}
                sx={{ overflow: "hidden" }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );
}
